import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * What the phone top bar shows for the current screen.
 *
 * A screen calls `useMobileChrome({ title, actions… }, deps)`; when it unmounts its settings
 * are dropped and the bar falls back to the route's own name from the nav model.
 *
 * Two contexts on purpose: screens only see the (stable) setter, so a title change never
 * re-renders the screen that set it — only the top bar reads the config.
 */
export interface ChromeConfig {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Where «رجوع» goes when there is no in-app history. `null` hides the back button. */
  backTo?: string | null;
  /** Extra icon buttons at the end of the bar (search, filter, …). */
  actions?: React.ReactNode;
}

type Setter = (owner: number, config: ChromeConfig | null) => void;

const ConfigCtx = createContext<ChromeConfig>({});
const SetterCtx = createContext<Setter | null>(null);

export const ChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entry, setEntry] = useState<{ owner: number; config: ChromeConfig } | null>(null);
  const set = useCallback<Setter>((owner, config) => {
    setEntry((cur) => {
      if (config) return { owner, config };
      return cur && cur.owner === owner ? null : cur;
    });
  }, []);
  return (
    <SetterCtx.Provider value={set}>
      <ConfigCtx.Provider value={entry?.config || EMPTY}>{children}</ConfigCtx.Provider>
    </SetterCtx.Provider>
  );
};

const EMPTY: ChromeConfig = {};

export const useChromeConfig = (): ChromeConfig => useContext(ConfigCtx);

let ownerSeq = 0;

/**
 * Set the phone top bar for this screen. The config is applied in an effect keyed on `deps`
 * (like useEffect), so list what the title/actions depend on.
 */
export function useMobileChrome(config: ChromeConfig, deps: React.DependencyList): void {
  const set = useContext(SetterCtx);
  const owner = useRef(0);
  if (!owner.current) owner.current = ++ownerSeq;
  const latest = useRef(config);
  latest.current = config;
  useEffect(() => {
    set?.(owner.current, latest.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    const id = owner.current;
    return () => set?.(id, null);
  }, [set]);
}

/* ── Toasts ────────────────────────────────────────────────────────────── */

export interface ToastInput {
  message: React.ReactNode;
  tone?: 'default' | 'success' | 'danger';
  /** Optional single action, e.g. «تراجع». */
  action?: { label: string; onClick: () => void };
  ms?: number;
}

/** Show a short message above the tab bar. Safe to call from anywhere (no hook needed). */
export function toast(input: ToastInput | string): void {
  const detail: ToastInput = typeof input === 'string' ? { message: input } : input;
  window.dispatchEvent(new CustomEvent('m-toast', { detail }));
}
